import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { ZonaProvider } from "@/context/ZonaContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import MiTrabajo from "@/pages/MiTrabajo";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Budget from "@/pages/Budget";
import Resources from "@/pages/Resources";
import Reports from "@/pages/Reports";
import Executive from "@/pages/Executive";
import Assignments from "@/pages/Assignments";
import Backlog from "@/pages/Backlog";
import Offers from "@/pages/Offers";
import Checklists from "@/pages/Checklists";
import QualityIncidents from "@/pages/QualityIncidents";
import OfferDetail from "@/pages/OfferDetail";
import InnovacionDigital from "@/pages/InnovacionDigital";
import PocAi from "@/pages/PocAi";
import WeeklySummary from "@/pages/WeeklySummary";
import SprintDashboard from "@/pages/SprintDashboard";
import DevOpsSync from "@/pages/DevOpsSync";
import ResourceReport from "@/pages/ResourceReport";
import Admin from "@/pages/Admin";
import AuditLog from "@/pages/AuditLog";
import Committee from "@/pages/Committee";
import Adjudicaciones from "@/pages/Adjudicaciones";
import CatalogoServicios from "@/pages/CatalogoServicios";
import ItPeticiones from "@/pages/ItPeticiones";
import PublicItPeticion from "@/pages/PublicItPeticion";
import SeguimientoItPeticion from "@/pages/SeguimientoItPeticion";
import WowDisplay from "@/pages/WowDisplay";
import IntegrationIT from "@/pages/IntegrationIT";
import IntegrationPublic from "@/pages/IntegrationPublic";
import Manuals from "@/pages/Manuals";
import AppCatalog from "@/pages/AppCatalog";
import Licitaciones from "@/pages/Licitaciones";
import LicitacionDetail from "@/pages/LicitacionDetail";
import ORAZaragoza from "@/pages/ORAZaragoza";
import Tender360 from "@/pages/Tender360";
import OfertasComercial from "@/pages/OfertasComercial";
import YtdBoard from "@/pages/YtdBoard";  // Iter230
import BusinessReview from "@/pages/BusinessReview";  // Entrega D 12/10/2026
import ClientesView from "@/pages/ClientesView";  // CRM-R06 · Clientes 360
import HitRateZonas from "@/pages/HitRateZonas";  // Iter282
import Cockpit from "@/pages/Cockpit";  // Cockpit Ejecutivo (portada 60s)
import ResultadosPorZona from "@/pages/ResultadosPorZona";  // Z02 · zona comercial
import ZonasAdmin from "@/pages/ZonasAdmin";  // Z01 · catálogo zonas
import TenderIntelligence from "@/pages/TenderIntelligence";  // Iter187
import MercadoSectorial from "@/pages/MercadoSectorial";  // Iter277
import GestionHoras from "@/pages/GestionHoras";
import AIHistoryPage from "@/pages/AIHistoryPage";
import Delegado from "@/pages/Delegado";
import Arrive from "@/pages/Arrive";
import SolicitudDemanda from "@/pages/SolicitudDemanda";
import SeguimientoDemanda from "@/pages/SeguimientoDemanda";
import Demanda from "@/pages/Demanda";
import ForcePasswordChangeModal from "@/components/ForcePasswordChangeModal";
import "@/App.css";

const PrivateRoute = ({ children, adminOnly }) => {
  const { user, loading, forcePasswordChange } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  // Restricción de Delegado Regional: solo puede acceder a /delegado
  if (user.role === "delegado_regional" && !location.pathname.startsWith("/delegado")) {
    return <Navigate to="/delegado" replace />;
  }
  // Restricción de Gestor Checklists: solo puede acceder a /integration-it
  if (user.role === "checklist_manager" && !location.pathname.startsWith("/integration-it")) {
    return <Navigate to="/integration-it" replace />;
  }
  return (
    <>
      {children}
      {forcePasswordChange && <ForcePasswordChangeModal />}
    </>
  );
};

// Después del login los usuarios van a su vista por defecto según su rol
const OfferManagerRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "delegado_regional") return <Navigate to="/delegado" replace />;
  if (user?.role === "checklist_manager") return <Navigate to="/integration-it" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <NotificationProvider>
        <ZonaProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/solicitar-it" element={<PublicItPeticion />} />
            <Route path="/seguimiento-it" element={<SeguimientoItPeticion />} />
            <Route path="/integration/:slug" element={<IntegrationPublic />} />
            <Route path="/wow" element={<WowDisplay />} />
            <Route path="/delegado" element={<PrivateRoute><Delegado /></PrivateRoute>} />
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<OfferManagerRedirect />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="mi-trabajo" element={<MiTrabajo />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="budget" element={<Budget />} />
              <Route path="resources" element={<Resources />} />
              <Route path="reports" element={<Reports />} />
              <Route path="backlog" element={<Backlog />} />
              <Route path="checklists" element={<Checklists />} />
              <Route path="calidad" element={<QualityIncidents />} />
              <Route path="offers" element={<Offers />} />
              <Route path="offers/:id" element={<OfferDetail />} />
              <Route path="executive" element={<Executive />} />
              <Route path="assignments" element={<Assignments />} />
              <Route path="devops" element={<DevOpsSync />} />
              <Route path="weekly-summary" element={<WeeklySummary />} />
              <Route path="sprint-dashboard" element={<SprintDashboard />} />
              <Route path="resource-report" element={<ResourceReport />} />
              <Route path="committee" element={<Committee />} />
              <Route path="adjudicaciones" element={<Adjudicaciones />} />
              <Route path="catalogo-servicios" element={<CatalogoServicios />} />
              <Route path="it-peticiones" element={<ItPeticiones />} />
              <Route path="manuales" element={<Manuals />} />
              <Route path="innovacion" element={<InnovacionDigital />} />
              <Route path="poc-ai" element={<PocAi />} />
              <Route path="catalogo-aplicaciones" element={<AppCatalog />} />
              <Route path="licitaciones" element={<Licitaciones />} />
              <Route path="licitaciones/:id" element={<LicitacionDetail />} />
              <Route path="ora-zaragoza" element={<ORAZaragoza />} />
              <Route path="tender360" element={<Tender360 />} />
              <Route path="licita" element={<Tender360 />} />
              <Route path="ai-history" element={<AIHistoryPage />} />
              <Route path="ofertas-comercial" element={<OfertasComercial />} />
              <Route path="clientes-360" element={<ClientesView />} />  {/* CRM-R06 */}
              <Route path="ytd-board" element={<YtdBoard />} />  {/* Iter230 */}
              <Route path="business-review" element={<BusinessReview />} />  {/* Entrega D 12/10/2026 */}
              <Route path="hit-rate-zonas" element={<HitRateZonas />} />  {/* Iter282 */}
              <Route path="cockpit" element={<Cockpit />} />  {/* Cockpit Ejecutivo */}
              <Route path="resultados-por-zona" element={<ResultadosPorZona />} />  {/* Z02 */}
              <Route path="zonas-admin" element={<ZonasAdmin />} />  {/* Z01 */}
              <Route path="tender-intelligence" element={<TenderIntelligence />} />
              <Route path="mercado-sectorial" element={<MercadoSectorial />} />
              <Route path="horas" element={<GestionHoras />} />
              <Route path="integration-it" element={<IntegrationIT />} />
              <Route path="arrive" element={<Arrive />} />
              <Route path="arrive/:id" element={<Arrive />} />
              <Route path="solicitud-demanda" element={<SolicitudDemanda />} />
              <Route path="seguimiento-demanda/:id" element={<SeguimientoDemanda />} />
              <Route path="demanda" element={<Demanda />} />
              <Route path="admin" element={<PrivateRoute adminOnly><Admin /></PrivateRoute>} />
              <Route path="admin/audit-log" element={<PrivateRoute adminOnly><AuditLog /></PrivateRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        </ZonaProvider>
        <Toaster position="top-right" richColors />
      </NotificationProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
